import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import config from "../../config";
import makeRequest from "../../makeRequest";

import shapeImg1 from "../../assets/img/hero/shape-4-1.png";
import shapeImg2 from "../../assets/img/hero/shape-4-2.png";
import heroImgFallback from "../../assets/img/hero/hero-4-1.jpg";

const BASE = config.api.baseUrl;

/* ─── Static fallback ─────────────────────────────────────────── */
const FallbackSlide = () => (
  <div className="mh-slide">
    <img src={heroImgFallback} alt="Maroof International Hospital" className="mh-slide__bg" />
    <div className="mh-slide__overlay" />
    <div className="container h-100">
      <div className="row h-100 align-items-center">
        <div className="col-lg-8 col-xl-7">
          <div className="mh-slide__content">
            <span className="mh-slide__badge">World-Class Healthcare</span>
            <h1 className="mh-slide__title">
              Providing the Best
              <br />
              <span>Medical Consultation</span>
            </h1>
            <p className="mh-slide__desc">Compassionate care backed by cutting-edge technology and expert physicians.</p>
            <div className="mh-slide__btns">
              <Link to="/about-us" className="mh-btn mh-btn--primary">
                Discover More
              </Link>
              <Link to="/contact-us" className="mh-btn mh-btn--outline">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Main component ───────────────────────────────────────────── */
const Hero = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    makeRequest({ route: "/website/page/public?name=Home&key=slider&isActive=1", method: "get" })
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
          setSlides(sorted);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="mh-hero">
      {/* Decorative shapes */}
      <img src={shapeImg1} alt="" className="mh-hero__shape mh-hero__shape--1" />
      <img src={shapeImg2} alt="" className="mh-hero__shape mh-hero__shape--2" />

      {loading || slides.length === 0 ? (
        <FallbackSlide />
      ) : (
        <>
          {/* Custom arrow buttons */}
          <button ref={prevRef} className="mh-arrow mh-arrow--prev" aria-label="Previous slide">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button ref={nextRef} className="mh-arrow mh-arrow--next" aria-label="Next slide">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>

          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            autoplay={{ delay: 5500, disableOnInteraction: false }}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            onSwiper={(swiper) => {
              // Re-attach custom nav refs after init
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }}
            pagination={{ clickable: true, el: ".mh-dots" }}
            loop={slides.length > 1}
            speed={800}
            className="mh-swiper"
          >
            {slides.map((slide) => (
              <SwiperSlide key={slide.id}>
                <div className="mh-slide">
                  {slide.hasImage ? (
                    <img
                      src={`${BASE}/website/page/${slide.id}/image`}
                      alt={slide.value || "Slide"}
                      className="mh-slide__bg"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <img src={heroImgFallback} alt="Slide" className="mh-slide__bg" />
                  )}
                  <div className="mh-slide__overlay" />
                  <div className="container h-100">
                    <div className="row h-100 align-items-center">
                      <div className="col-lg-8 col-xl-7">
                        <div className="mh-slide__content">
                          <span className="mh-slide__badge">Maroof International Hospital</span>
                          {slide.value && <h1 className="mh-slide__title">{slide.value}</h1>}
                          {slide.description && <p className="mh-slide__desc">{slide.description}</p>}
                          {(slide.buttonName || slide.navigationLink || slide.buttonLink) && (
                            <div className="mh-slide__btns">
                              {slide.isExternal ? (
                                <a href={slide.buttonLink} target="_blank" rel="noopener noreferrer" className="mh-btn mh-btn--primary">
                                  {slide.buttonName || "Learn More"}
                                </a>
                              ) : (
                                <Link to={slide.navigationLink || slide.buttonLink || "/about-us"} className="mh-btn mh-btn--primary">
                                  {slide.buttonName || "Learn More"}
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Pagination dots rendered outside swiper so they sit on top */}
          <div className="mh-dots" />
        </>
      )}

      <style>{`
        /* ── Section wrapper ── */
        .mh-hero {
          position: relative;
          width: 100%;
          overflow: hidden;
          background: #03152b;
        }
        .mh-hero__shape {
          position: absolute;
          z-index: 10;
          pointer-events: none;
        }
        .mh-hero__shape--1 { top: 0; left: 0; opacity: .6; }
        .mh-hero__shape--2 { bottom: 0; right: 0; opacity: .5; }

        /* ── Swiper container ── */
        .mh-swiper {
          width: 100%;
          height: 680px;
        }
        @media (max-width: 991px) { .mh-swiper { height: 520px; } }
        @media (max-width: 575px) { .mh-swiper { height: 420px; } }

        /* ── Individual slide ── */
        .mh-slide {
          position: relative;
          width: 100%;
          height: 680px;
          overflow: hidden;
        }
        @media (max-width: 991px) { .mh-slide { height: 520px; } }
        @media (max-width: 575px) { .mh-slide { height: 420px; } }

        .mh-slide__bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 0;
        }

        /* Gradient overlay — strong on left, lighter on right */
        .mh-slide__overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: linear-gradient(
            105deg,
            rgba(2, 15, 35, 0.82) 0%,
            rgba(2, 15, 35, 0.55) 55%,
            rgba(2, 15, 35, 0.15) 100%
          );
        }

        .mh-slide .container {
          position: relative;
          z-index: 2;
          height: 100%;
        }
        .mh-slide .row { height: 100%; }

        /* ── Slide content ── */
        .mh-slide__content {
          padding: 40px 0;
          animation: mhFadeUp .7s ease both;
        }

        @keyframes mhFadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Badge / label */
        .mh-slide__badge {
          display: inline-block;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.25);
          backdrop-filter: blur(6px);
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 6px 18px;
          border-radius: 50px;
          margin-bottom: 22px;
        }

        /* Heading */
        .mh-slide__title {
          font-size: clamp(2rem, 4.5vw, 3.4rem);
          font-weight: 800;
          line-height: 1.18;
          color: #ffffff;
          margin-bottom: 20px;
          text-shadow: 0 2px 20px rgba(0,0,0,.3);
        }
        .mh-slide__title span {
          color: #3fb65f;
          display: block;
        }

        /* Description */
        .mh-slide__desc {
          font-size: clamp(.95rem, 1.5vw, 1.1rem);
          color: rgba(255,255,255,.82);
          line-height: 1.75;
          max-width: 540px;
          margin-bottom: 36px;
        }

        /* CTA buttons */
        .mh-slide__btns { display: flex; gap: 14px; flex-wrap: wrap; }

        .mh-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          border-radius: 50px;
          font-size: .93rem;
          font-weight: 700;
          text-decoration: none;
          transition: all .3s ease;
          letter-spacing: .4px;
          white-space: nowrap;
        }
        .mh-btn--primary {
          background: #3fb65f;
          color: #fff;
          box-shadow: 0 8px 24px rgba(63,182,95,.35);
        }
        .mh-btn--primary:hover {
          background: #33a052;
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(63,182,95,.45);
        }
        .mh-btn--outline {
          background: transparent;
          color: #fff;
          border: 2px solid rgba(255,255,255,.55);
        }
        .mh-btn--outline:hover {
          background: rgba(255,255,255,.12);
          border-color: #fff;
          color: #fff;
          transform: translateY(-2px);
        }

        /* ── Navigation arrows ── */
        .mh-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          width: 52px;
          height: 52px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,.4);
          background: rgba(255,255,255,.08);
          backdrop-filter: blur(8px);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all .3s ease;
        }
        .mh-arrow:hover {
          background: #3fb65f;
          border-color: #3fb65f;
          box-shadow: 0 6px 20px rgba(63,182,95,.4);
        }
        .mh-arrow svg { width: 22px; height: 22px; }
        .mh-arrow--prev { left: 24px; }
        .mh-arrow--next { right: 24px; }
        .mh-arrow.swiper-button-disabled {
          opacity: .35;
          pointer-events: none;
        }
        @media (max-width: 575px) {
          .mh-arrow { width: 40px; height: 40px; }
          .mh-arrow svg { width: 18px; height: 18px; }
          .mh-arrow--prev { left: 10px; }
          .mh-arrow--next { right: 10px; }
        }

        /* ── Pagination dots ── */
        .mh-dots {
          position: absolute;
          bottom: 28px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 20;
          display: flex;
          gap: 8px;
        }
        .mh-dots .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          border-radius: 50px;
          background: rgba(255,255,255,.45);
          opacity: 1;
          transition: all .35s ease;
          cursor: pointer;
        }
        .mh-dots .swiper-pagination-bullet-active {
          background: #3fb65f;
          width: 28px;
        }
      `}</style>
    </section>
  );
};

export default Hero;
